'use client';

import { CustomImage as Image } from '..';
import { useRef, useState } from 'react';
import {
  CurrencySymbol,
  formatPriceWithComma,
  getCurrencySymbolFromEnum,
} from 'hooks/pricing/useFormattedPrice';
import { useCartStore, useShallow, DotSpinner } from '..';
import { useClickOutside } from 'hooks/dom/useClickOutside';
import { useTranslations } from 'next-intl';
import { ProductType } from 'components/shared/Cart/types';
import { Currency } from 'utils/Currency';
import {
  ShopProductCardRoot,
  ShopCardBody,
  ShopCardBottomSheet,
  ShopCardFooterRow,
  ShopCardMedia,
  ShopCardMediaDimBackdrop,
  ShopCardPriceCluster,
  ShopCardPrimaryCta,
  ShopCardSpecSection,
  ShopCardTag,
  ShopCardTagText,
} from './shopProductCard';
import styles from './ProductCardColorAccessory.module.css';

export type ColorAccessoryVariant = {
  key: string;
  name: string;
  swatch: string;
  mainImage: string;
  price: number;
  shopifyVariantId: string;
};

export type ProductCardColorAccessoryProps = {
  className?: string;
  productType: ProductType;
  title: string;
  description: string;
  colors: ColorAccessoryVariant[];
  tag?: string;
  originalPrice?: string;
  discountPercentage?: string;
  isFirst?: boolean;
};

/**
 * Shop card for single-SKU-per-color accessories (no size options):
 * color-swatch overlay like ProductCardRing, but adds to cart directly
 * from the color step.
 */
export const ProductCardColorAccessory = ({
  className,
  productType,
  title,
  description,
  colors,
  tag,
  originalPrice,
  discountPercentage,
  isFirst = false,
}: ProductCardColorAccessoryProps) => {
  const CartClient = useCartStore(
    useShallow((s) => ({
      currency: s.currency,
      taxString: s.taxString,
      addToCart: s.addToCart,
    })),
  );
  const defaultColorKey = colors[0]?.key ?? null;
  const [isOpen, setIsOpen] = useState(false);
  const [selectedColorKey, setSelectedColorKey] = useState<string | null>(
    defaultColorKey,
  );
  const [isLoading, setIsLoading] = useState(false);
  const overlayRef = useRef<HTMLDivElement>(null);
  const tCart = useTranslations('cart');
  const t = useTranslations('ring');

  const selectedColor = colors.find((c) => c.key === selectedColorKey) ?? null;
  const heroColor = selectedColor ?? colors[0];
  const displayPrice =
    selectedColor?.price ?? Math.min(...colors.map((c) => c.price));
  const currency = (CartClient.currency ?? Currency.usd) as Currency;

  const closeOverlay = () => {
    setIsOpen(false);
    setSelectedColorKey(defaultColorKey);
  };

  useClickOutside(overlayRef, () => setTimeout(closeOverlay, 200), isOpen);

  const handleAddToCart = async () => {
    if (!selectedColor) return;
    try {
      setIsLoading(true);
      await CartClient.addToCart([
        {
          shopifyVariantId: selectedColor.shopifyVariantId,
          productType,
        },
      ]);
    } finally {
      setIsLoading(false);
      closeOverlay();
    }
  };

  return (
    <ShopProductCardRoot
      className={[styles.root, className].filter(Boolean).join(' ')}
    >
      <ShopCardMedia className={styles.media} onClick={() => closeOverlay()}>
        {heroColor?.mainImage && (
          <Image
            src={heroColor.mainImage}
            alt={`${title} — ${heroColor.name}`}
            height={448}
            width={300}
            sizes="(min-width: 1024px) calc((100% - 48px) / 3), 100vw"
            priority={isFirst}
            style={{ width: '100%', objectFit: 'cover' }}
          />
        )}
      </ShopCardMedia>
      <ShopCardBody>
        <ShopCardSpecSection className={`ringSpec ${styles.ringSpec}`}>
          <h3 className="title">{title}</h3>
          {description && <p className="description">{description}</p>}
          <br />
        </ShopCardSpecSection>
        <ShopCardFooterRow
          aria-hidden={isOpen}
          style={isOpen ? { visibility: 'hidden' } : undefined}
        >
          <ShopCardPriceCluster
            className={`pricing-info ${styles.pricingInfo}`}
          >
            {originalPrice && discountPercentage && (
              <span className="striked">
                {CurrencySymbol[currency]}
                {formatPriceWithComma(parseFloat(originalPrice))}
              </span>
            )}
            {CurrencySymbol[currency]}
            {formatPriceWithComma(
              originalPrice && discountPercentage
                ? displayPrice * (1 - parseFloat(discountPercentage) / 100)
                : displayPrice,
            )}
            <span>{CartClient.taxString} </span>
          </ShopCardPriceCluster>
          <ShopCardPrimaryCta
            className={isLoading ? 'quickAdd disable' : 'quickAdd'}
            data-product={title}
            data-buttontype="show color"
            onClick={(e) => {
              e.stopPropagation();
              setIsOpen(true);
            }}
            disabled={isLoading}
          >
            {tCart('sharedCart.upsell.addToCart')}
          </ShopCardPrimaryCta>
        </ShopCardFooterRow>
      </ShopCardBody>
      {tag ? <ShopCardTag $heightMode="ring"><ShopCardTagText tag={tag} /></ShopCardTag> : null}

      <ShopCardMediaDimBackdrop className={isOpen ? 'open' : ''} />

      <ShopCardBottomSheet
        className={`sizeOverlay ${isOpen ? 'open' : ''} ${styles.bottomSheet}`}
        ref={overlayRef}
        aria-hidden={!isOpen}
      >
        <div className="stepContent">
          <div className="stepHeader">
            <div className="heading">{t('packs.color')}</div>
          </div>
          <div className="colorSwatches">
            {colors.map((c) => (
              <button
                key={c.key}
                className={`color-swatch-btn ${
                  selectedColorKey === c.key ? 'active' : ''
                }`}
                data-product={title}
                data-buttontype="select color"
                onClick={() => setSelectedColorKey(c.key)}
                aria-label={c.name}
                aria-pressed={selectedColorKey === c.key}
              >
                <Image
                  src={c.swatch}
                  alt={c.name}
                  width={48}
                  height={48}
                  style={{
                    width: 48,
                    height: 48,
                    borderRadius: '50%',
                    objectFit: 'cover',
                  }}
                />
              </button>
            ))}
          </div>
          {selectedColor && (
            <div className="selectedColorName">{selectedColor.name}</div>
          )}
          <button
            className="addToCart"
            data-product={title}
            data-buttontype="add to cart"
            data-variantId={selectedColor?.shopifyVariantId ?? ''}
            disabled={!selectedColor || isLoading}
            onClick={handleAddToCart}
            aria-busy={isLoading}
          >
            {isLoading ? (
              <DotSpinner color="#FFFFFF" />
            ) : (
              <>
                {tCart('sharedCart.upsell.addToCartFor', {
                  price: `${getCurrencySymbolFromEnum(currency)}${formatPriceWithComma(selectedColor?.price ?? displayPrice)}`,
                })}
              </>
            )}
          </button>
        </div>
      </ShopCardBottomSheet>
    </ShopProductCardRoot>
  );
};
